import * as signalR from "@microsoft/signalr";
import { domain } from "../util/Apiconfig";
import { getUserToken } from "../function/AuthStorage";

/**
 * تحديث 59 — الاتصال الفوري بمركز التواصل (SignalR /hubs/comm): اتصال واحد مشترك لكل الشاشات.
 * الأحداث: message · messageUpdated · messageDeleted · unread · read · rooms · room · circular · circularStats.
 * عند انقطاع الاتصال تعمل الشاشات بالاستطلاع الدوري (polling) وتُعاد المزامنة تلقائياً بعد عودته.
 */
const EVENTS = ["message", "messageUpdated", "messageDeleted", "unread", "read", "rooms", "room", "circular", "circularStats"];
const listeners = new Map();
const statusListeners = new Set();
const joined = new Set();
let conn = null;
let starting = null;
let status = "offline";

function emit(event, payload) {
  (listeners.get(event) || []).forEach((fn) => { try { fn(payload); } catch { /* مستمع واحد لا يكسر البقية */ } });
}
function setStatus(s) {
  status = s;
  statusListeners.forEach((fn) => { try { fn(s); } catch { /* */ } });
}

function build() {
  const c = new signalR.HubConnectionBuilder()
    .withUrl(`${domain}/hubs/comm`, { accessTokenFactory: () => getUserToken() || "", withCredentials: false })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 20000, 30000])
    .configureLogging(signalR.LogLevel.None)
    .build();
  EVENTS.forEach((e) => c.on(e, (p) => emit(e, p)));
  c.onreconnecting(() => setStatus("reconnecting"));
  c.onreconnected(async () => {
    setStatus("live");
    for (const id of joined) { try { await c.invoke("JoinRoom", id); } catch { /* */ } }
    emit("reconnected", {});
  });
  c.onclose(() => { if (conn === c) { setStatus("offline"); conn = null; } });
  return c;
}

/** يبدأ الاتصال (مرة واحدة). يعيد true عند النجاح، وfalse ليعمل الاستطلاع الدوري بديلاً. */
let connToken = null;
let retry = null;

export async function connectComm() {
  const token = getUserToken();
  if (!token) { if (conn) await disconnectComm(); return false; }
  // تغيّر المستخدم (خروج ثم دخول بحساب آخر): اتصال جديد بقنوات المستخدم الجديد
  if (conn && connToken && connToken !== token && !starting) {
    try { await conn.stop(); } catch { /* */ }
    conn = null; joined.clear();
  }
  if (conn && conn.state === signalR.HubConnectionState.Connected) return true;
  if (starting) return starting;
  const c = conn || build();
  conn = c;
  connToken = token;
  setStatus("connecting");
  starting = c.start()
    .then(async () => {
      setStatus("live");
      for (const id of joined) { try { await c.invoke("JoinRoom", id); } catch { /* */ } }
      return true;
    })
    .catch(() => {
      if (conn === c) { setStatus("offline"); conn = null; }
      // إعادة المحاولة لاحقاً (الشاشات تعمل بالاستطلاع الدوري حتى يعود الاتصال)
      clearTimeout(retry); retry = setTimeout(() => { if (getUserToken() && !conn) connectComm(); }, 30000);
      return false;
    })
    .finally(() => { starting = null; });
  return starting;
}

export function onComm(event, fn) {
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event).add(fn);
  return () => listeners.get(event)?.delete(fn);
}

export function onCommStatus(fn) {
  statusListeners.add(fn);
  fn(status);
  return () => statusListeners.delete(fn);
}

export const commStatus = () => status;

/** متابعة غرفة مفتوحة: الخادم يتحقق من حق القراءة قبل الانضمام. */
export async function joinRoom(id) {
  joined.add(id);
  if (await connectComm()) { try { return await conn.invoke("JoinRoom", id); } catch { return false; } }
  return false;
}

export async function leaveRoom(id) {
  joined.delete(id);
  if (conn && conn.state === signalR.HubConnectionState.Connected) { try { await conn.invoke("LeaveRoom", id); } catch { /* */ } }
}

export async function disconnectComm() {
  joined.clear();
  if (conn) { try { await conn.stop(); } catch { /* */ } }
  conn = null;
  setStatus("offline");
}
